import { ExpandableProductCard } from 'components/products/journey';
import { LoadingSpinner } from 'components/ui';
import { isEqual, omit } from 'lodash';
import querystring from 'querystring';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import { useVirtual } from 'react-virtual';
import type { JourneyOrdersPath } from 'store/services/types';
import { useParseURLQueryParams } from 'utils';
import { JourneyContext } from './JourneyContext';

interface SecondOrderContainerProps {
  data: JourneyOrdersPath[] | undefined;
  isFetching: boolean;
}
export const SecondOrderContainer = ({
  data,
  isFetching,
}: SecondOrderContainerProps) => {
  const { query } = useParseURLQueryParams();

  const { secondProduct, setSecondProduct } = useContext(JourneyContext);

  const history = useHistory();

  interface HandleSelect {
    product: JourneyOrdersPath;
    cardOpen: boolean;
  }
  const handleSelect = ({ product, cardOpen }: HandleSelect) => {
    if (isEqual(product, secondProduct) && cardOpen) {
      setSecondProduct(undefined);
      history.push({
        search: querystring.stringify(
          omit(
            {
              ...query,
            },
            'second_product_id'
          )
        ),
      });
      return;
    }

    setSecondProduct(product);
    history.push({
      search: querystring.stringify(
        omit(
          {
            ...query,
            second_product_id: product.id,
          },
          ['third_product_id']
        )
      ),
    });
  };

  useEffect(() => {
    if (query.second_product_id) {
      const selectedProduct = data?.find(
        (item) => String(item.id) === query.second_product_id
      );
      setSecondProduct(selectedProduct);
      return;
    }

    if (!!!query.second_product_id) {
      setSecondProduct(undefined);
    }
  }, [data, query.second_product_id, setSecondProduct]);

  const parentRef = useRef();
  const rowVirtualizer = useVirtual({
    size: data?.length || 0,
    parentRef,
    estimateSize: useCallback(
      (index) => {
        if (!!!secondProduct?.id) return 100;
        if (data && data[index]?.id === secondProduct?.id) {
          return 185;
        } else {
          return 100;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [secondProduct?.id]
    ),
  });

  return (
    <div className="pr-1 border-r">
      {query.first_product_id ? (
        isFetching ? (
          <div className="grid h-full place-items-center">
            <LoadingSpinner className="w-16 h-16 text-segments-accent-color-orange" />
          </div>
        ) : (
          <div
            //@ts-ignore
            ref={parentRef}
            className="pr-2 scrollbar-thin scrollbar-thumb-segments-darker-gray-75 scrollbar-thumb-rounded-lg scrollable-parent"
            style={{
              height: `780px`,
              width: `100%`,
              overflow: 'auto',
            }}
          >
            <div
              className="divide-y"
              style={{
                height: `${rowVirtualizer.totalSize}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.virtualItems.map((virtualRow) => (
                <div
                  key={virtualRow.index}
                  className="py-2 virtualized-child"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <ExpandableProductCard
                    data={data && data[virtualRow.index]}
                    key={data && data[virtualRow.index].id}
                    selected={secondProduct}
                    setSelected={handleSelect}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <div className="grid h-full text-black place-items-center text-opacity-30">
          <p>Select a product 1st order.</p>
        </div>
      )}
    </div>
  );
};
